import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Check, X, Camera, FileText, Share2, Tag, CreditCard, Globe, UserCheck, ChevronRight
} from "lucide-react";

const ProfileScoreCard = ({ profile, connectStatus }) => {
  const { t } = useTranslation(['dashboard']);
  const navigate = useNavigate();

  // Define all profile completion criteria
  const criteria = [
    {
      id: "photo",
      label: t('dashboard:creator.profileScore.photo'),
      icon: Camera,
      completed: Boolean(profile?.avatar_url),
      weight: 15,
    },
    {
      id: "bio",
      label: t('dashboard:creator.profileScore.bio'),
      icon: FileText,
      completed: Boolean(profile?.bio && profile.bio.length >= 20),
      weight: 20,
    },
    {
      id: "social",
      label: t('dashboard:creator.profileScore.social'),
      icon: Share2,
      completed: Array.isArray(profile?.social_platforms) && profile.social_platforms.length > 0,
      weight: 20,
    },
    {
      id: "niches",
      label: t('dashboard:creator.profileScore.niches'),
      icon: Tag,
      completed: Array.isArray(profile?.niches) && profile.niches.length > 0,
      weight: 15,
    },
    {
      id: "payout",
      label: t('dashboard:creator.profileScore.payout'),
      icon: CreditCard,
      completed: Boolean(connectStatus?.payouts_enabled),
      weight: 15,
    },
    {
      id: "location",
      label: t('dashboard:creator.profileScore.location'),
      icon: Globe,
      completed: Boolean(profile?.location),
      weight: 10,
    },
    {
      id: "audience",
      label: t('dashboard:creator.profileScore.audience'),
      icon: UserCheck,
      completed: Boolean(profile?.audience_type),
      weight: 5,
    },
  ];

  // Calculate total score
  const totalScore = criteria.reduce((sum, item) => {
    return sum + (item.completed ? item.weight : 0);
  }, 0);

  const completedCount = criteria.filter(c => c.completed).length;
  const missingItems = criteria.filter(c => !c.completed);

  // Determine score color
  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-400";
    if (score >= 50) return "text-cyan-400";
    if (score >= 30) return "text-yellow-400";
    return "text-red-400";
  };

  const getProgressColor = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 50) return "bg-cyan-500";
    if (score >= 30) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <div className="glass-card rounded-2xl p-6" data-testid="profile-score-card">
      {/* Header with Score */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold font-['Syne'] text-white mb-1">
            {t('dashboard:creator.profileScore.title')}
          </h3>
          <p className="text-sm text-gray-400">
            {t('dashboard:creator.profileScore.subtitle')}
          </p>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold font-['Unbounded'] ${getScoreColor(totalScore)}`}>
            {totalScore}%
          </div>
          <div className="text-xs text-gray-500">
            {completedCount}/{criteria.length} {t('dashboard:creator.profileScore.completed')}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-white/10 rounded-full mb-6 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${getProgressColor(totalScore)}`}
          style={{ width: `${totalScore}%` }}
        />
      </div>

      {/* Checklist Items */}
      <div className="space-y-3 mb-6">
        {criteria.map((item) => (
          <div
            key={item.id}
            className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
              item.completed 
                ? "bg-green-500/10 border border-green-500/20" 
                : "bg-white/5 border border-white/10 hover:border-white/20"
            }`}
            data-testid={`profile-criteria-${item.id}`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              item.completed ? "bg-green-500/20" : "bg-white/10"
            }`}>
              <item.icon className={`w-4 h-4 ${
                item.completed ? "text-green-400" : "text-gray-500"
              }`} />
            </div>
            <div className="flex-1">
              <span className={`text-sm font-medium ${
                item.completed ? "text-green-400" : "text-gray-300"
              }`}>
                {item.label}
              </span>
              <span className="text-xs text-gray-500 ml-2">+{item.weight}%</span>
            </div>
            {item.completed ? (
              <Check className="w-5 h-5 text-green-400" />
            ) : (
              <X className="w-5 h-5 text-gray-500" />
            )}
          </div>
        ))}
      </div>

      {/* CTA for Incomplete Profiles */}
      {totalScore < 100 && (
        <div className="pt-4 border-t border-white/10">
          <div className="text-sm text-gray-400 mb-3">
            {missingItems.length === 1 
              ? t('dashboard:creator.profileScore.oneMore')
              : t('dashboard:creator.profileScore.itemsRemaining', { count: missingItems.length })
            }
          </div>
          <button
            onClick={() => navigate("/creator/profile")}
            data-testid="complete-profile-btn"
            className="w-full btn-primary py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
          >
            {t('dashboard:creator.profileScore.completeNow')}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Success State */}
      {totalScore === 100 && (
        <div className="pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-green-500/10 border border-green-500/20">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <Check className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <div className="text-sm font-semibold text-green-400">
                {t('dashboard:creator.profileScore.complete100')}
              </div>
              <div className="text-xs text-gray-400">
                {t('dashboard:creator.profileScore.complete100Desc')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScoreCard;
